import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  EmptyState,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type React from "react";
import { useState } from "react";

import { useChanges, useRunPiles, useRuns, useSectors, useSweep } from "../../api/generated/stockvol";
import type {
  ChangeOut,
  CoalOut,
  LabAttributeOut,
  PageRunOut,
  PileObsOut,
  SweepDetail,
  VolumeOut,
} from "../../api/generated/model";
import { useCoalQuality, useProductColours, useSiteInfo } from "../../api/hooks";
import { LegendChips } from "../../components/bits";
import {
  IconArrowsUpDown,
  IconChevronDown,
  IconMountain,
  IconRadar,
  IconStack2,
  IconWeight,
} from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { TableCard } from "../../components/Section";
import { PageSkeleton } from "../../components/Skeletons";
import { StatCard } from "../../components/StatCard";
import { labAttrs, labLine } from "../../lib/lab";
import { formatLocal } from "../../lib/time";
import { scaleOf } from "../../lib/units";
import { palette } from "../../palette";
import { LineageDrawer } from "./LineageDrawer";
import { type CoalQuality, PileTable } from "./PileTable";
import { PolarArc } from "./PolarArc";
import { SectorBars } from "./SectorBars";
import { SectorTable } from "./SectorTable";
import { fmt0, fmtPct, fmtRange, fmtSigned, pileRows, totals } from "./tables";

const data = <T,>(r: unknown) => (r as { data: T }).data;
const GRADE: Record<string, string> = { good: "teal", degraded: "yellow", poor: "red" };

/** The latest current run: the yard's numbers, the plan view, the piles, the sectors. */
export function Overview() {
  const site = useSiteInfo();
  const runs = useRuns({ current: true, limit: 1 }, { query: { select: data<PageRunOut>, refetchInterval: 30_000 } });
  const run = runs.data?.items[0];
  const id = run?.id ?? "";
  const on = { enabled: run !== undefined };
  const piles = useRunPiles(id, { crs: "site" }, { query: { ...on, select: data<PileObsOut[]> } });
  const sectors = useSectors(id, { query: { ...on, select: data<VolumeOut[]> } });
  const changes = useChanges(id, { query: { ...on, select: data<ChangeOut[]> } });
  const sweep = useSweep(run?.sweep_id ?? "", { query: { enabled: run !== undefined, select: data<SweepDetail> } });
  const coal = useCoalQuality(run?.id);
  const colourOf = useProductColours((piles.data ?? []).map((p) => p.product));
  const [pile, setPile] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [showTable, table] = useDisclosure(false);

  const head = (body: React.ReactNode) => (
    <Stack gap="lg">
      <PageHeader title="Overview" />
      {body}
    </Stack>
  );
  if (runs.isPending || site.isPending) return <PageSkeleton />;
  if (!site.data) return head(<Alert color="red">The site configuration is not available.</Alert>);
  if (!run)
    return head(
      <Card>
        <EmptyState
          icon={<IconRadar size={30} />}
          title="No results yet"
          description="No sweep has been processed yet. The first sweep of the stacker appears here a few minutes after it ends."
          py="xl"
        />
      </Card>,
    );
  const tz = site.data.timezone;
  const scale = scaleOf(site.data);
  const pileList = piles.data ?? [];
  const rows = pileRows(pileList, changes.data ?? [], scale);
  const t = totals(rows);
  const total = sectors.data?.find((v) => v.scope === "total");
  const yardChange = changes.data?.find((c) => c.scope === "total");
  const stacked = yardChange?.dv_stacked_m3 ?? t.stacked;
  const reclaimed = yardChange?.dv_reclaimed_m3 ?? t.reclaimed;
  const sw = sweep.data;
  const coverage = sw ? { from: sw.slew_start_deg, to: sw.slew_end_deg } : null;
  const grade = run.quality && "grade" in run.quality ? String(run.quality["grade"]) : null;
  const products = [...new Set(pileList.map((p) => p.product).filter((p): p is string => !!p))].sort();
  const tallest = pileList.reduce((m, p) => Math.max(m, p.h_max_m ?? 0), 0);
  const focus = pileList.find((p) => p.code === (hovered ?? pile));
  const attrs = labAttrs(site.data);
  const quality: CoalQuality | null = coal.data
    ? {
        piles: new Map(coal.data.piles.map((p) => [p.code, p.standing])),
        yard: coal.data.standing,
        attrs,
      }
    : null;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Overview"
        subtitle={`${site.data.name} · stacker ${site.data.stacker_code} · ${products.length} ${products.length === 1 ? "product" : "products"} on ${rows.length} ${rows.length === 1 ? "pile" : "piles"}`}
        actions={
          grade && (
            <Badge size="lg" variant="light" color={GRADE[grade] ?? "gray"} tt="none">
              Measurement quality: {grade}
            </Badge>
          )
        }
      />
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 5 }} spacing="md">
        <StatCard
          label="Yard volume"
          value={fmt0(total?.v_best_m3)}
          unit="m³"
          valueTestId="yard-volume"
          secondary={rows.length ? `piles ${fmtRange(t.vMin, t.vMax)} m³` : `range ${fmtRange(total?.v_min_m3, total?.v_max_m3)} m³`}
          icon={<IconStack2 size={20} />}
        />
        <StatCard
          label="Yard tonnes"
          value={fmt0(total?.tonnes_t)}
          unit="t"
          secondary={
            total?.density_t_m3 != null
              ? `ρ ${total.density_t_m3.toFixed(3)} t/m³ · ${total.density_source ?? ""}`
              : "density not known yet"
          }
          icon={<IconWeight size={20} />}
        />
        <StatCard
          label="Active piles"
          value={rows.length}
          secondary={`${products.length} ${products.length === 1 ? "product" : "products"}${tallest > 0 ? ` · tallest ${tallest.toFixed(1)}\u00a0m` : ""}`}
          icon={<IconMountain size={20} />}
        />
        <StatCard
          label="Last sweep"
          testId="last-sweep"
          value={<span style={{ fontSize: 16, whiteSpace: "nowrap" }}>{sw ? formatLocal(sw.ended_at, tz) : "—"}</span>}
          secondary={
            sw ? <span style={{ whiteSpace: "nowrap" }}>{`${sw.capture_status} · coverage ${fmtPct(sw.arc_coverage_frac)}`}</span> : ""
          }
          icon={<IconRadar size={20} />}
          tone={sw?.capture_status === "complete" ? "ok" : "warning"}
        />
        <StatCard
          label="Since the previous sweep"
          value={fmtSigned(stacked + reclaimed)}
          unit="m³"
          secondary={
            <Group gap={10} wrap="nowrap">
              <Text span size="xs" c="teal.8" style={{ whiteSpace: "nowrap" }}>
                {fmtSigned(stacked)} stacked
              </Text>
              <Text span size="xs" c="blue.8" style={{ whiteSpace: "nowrap" }}>
                {fmtSigned(reclaimed)} reclaimed
              </Text>
            </Group>
          }
          icon={<IconArrowsUpDown size={20} />}
        />
      </SimpleGrid>
      <Grid gap="md">
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card h="100%" pos="relative">
            <Stack gap={0} mb="xs">
              <Text fw={600} size="sm">
                Stockyard plan
              </Text>
              <Text size="xs" c="dimmed">
                North up · rings every 25 m from the pivot
              </Text>
            </Stack>
            <div style={{ position: "absolute", top: 16, right: 16, zIndex: 2, pointerEvents: "none" }}>
              <PileReadout pile={focus} colourOf={colourOf} coal={focus && quality?.piles.get(focus.code)} attrs={attrs} />
            </div>
            <PolarArc
              site={site.data}
              piles={pileList}
              coverage={coverage}
              boom={sw?.slew_end_deg ?? null}
              colourOf={colourOf}
              hovered={hovered}
              selected={pile}
              onHover={setHovered}
              onPile={setPile}
            />
            <Stack gap={6} mt="sm">
              <LegendChips items={products.map((p) => ({ label: p, colour: colourOf(p) }))} />
              <LegendChips
                items={[
                  { label: "Covered by the last sweep", colour: palette.plan.coverage },
                  { label: "Boom at the end of the sweep", colour: palette.plan.boom, shape: "line" },
                ]}
              />
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <TableCard
            title="Piles"
            actions={
              <Text size="xs" c="dimmed">
                along the arc from its start · click a pile for its history
              </Text>
            }
          >
            <PileTable
              rows={rows}
              colourOf={colourOf}
              hovered={hovered}
              onHover={setHovered}
              onPile={setPile}
              quality={quality}
            />
          </TableCard>
        </Grid.Col>
      </Grid>
      <Card>
        <Group justify="space-between" mb="sm">
          <Stack gap={0}>
            <Text fw={600} size="sm">
              Sectors
            </Text>
            <Text size="xs" c="dimmed">
              Volume of each of the {site.data.sectors.count} sectors along the arc
            </Text>
          </Stack>
          <Button
            variant="subtle"
            size="xs"
            onClick={table.toggle}
            rightSection={
              <IconChevronDown size={14} style={{ transform: showTable ? "rotate(180deg)" : undefined, transition: "transform 150ms" }} />
            }
          >
            {showTable ? "Hide table" : "Show table"}
          </Button>
        </Group>
        <SectorBars sectors={sectors.data ?? []} />
        <Collapse expanded={showTable}>
          <div className="sv-scroll" style={{ marginTop: 12 }}>
            <SectorTable sectors={sectors.data ?? []} />
          </div>
        </Collapse>
      </Card>
      <LineageDrawer
        code={pile}
        tz={tz}
        onClose={() => setPile(null)}
        coal={coal.data?.piles.find((p) => p.code === pile)}
        attrs={attrs}
      />
    </Stack>
  );
}

/** What the pointer is on: a pile's name, product and numbers (the plan view's corner). */
function PileReadout({
  pile,
  colourOf,
  coal,
  attrs,
}: {
  pile: PileObsOut | undefined;
  colourOf: (p: string | null | undefined) => string;
  coal?: CoalOut | undefined;
  attrs: LabAttributeOut[];
}) {
  if (!pile) return null;
  const v = pile.volume;
  return (
    <Paper withBorder radius="md" px="sm" py={6} shadow="xs" maw={260}>
      <Group gap={8} wrap="nowrap">
        <span
          aria-hidden="true"
          style={{ width: 10, height: 10, borderRadius: 3, background: colourOf(pile.product), flex: "none" }}
        />
        <Text size="sm" fw={600} truncate>
          {pile.label ?? pile.code}
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        {pile.product ?? "no product"} · {pile.code}
      </Text>
      <Text size="xs" mt={2}>
        {fmt0(v?.v_best_m3)} m³ · {fmt0(v?.tonnes_t)} t · {pile.h_max_m?.toFixed(1)} m high
      </Text>
      {coal && coal.coverage > 0 && (
        <Text size="xs" mt={2} data-testid="readout-lab">
          {labLine(coal.values, attrs)}
        </Text>
      )}
    </Paper>
  );
}
